import React, { useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Camera, X, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

async function fileToResizedBase64(fileOrDataUrl, maxDim = 768) {
  const dataUrl = typeof fileOrDataUrl === "string"
    ? fileOrDataUrl
    : await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(fileOrDataUrl);
      });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.9);
}

export default function FaceScanner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [makePublic, setMakePublic] = useState(true);
  const [limitHit, setLimitHit] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    const resized = await fileToResizedBase64(file);
    setPreview(resized);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCamOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 50);
    } catch (e) {
      toast.error("Camera access denied. Try uploading a photo instead.");
    }
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOpen(false);
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const data = canvas.toDataURL("image/jpeg", 0.9);
    const resized = await fileToResizedBase64(data);
    setPreview(resized);
    closeCamera();
  };

  const reset = () => {
    setPreview(null);
    setLimitHit(false);
  };

  const analyze = async () => {
    if (!preview) return;
    setScanning(true);
    setLimitHit(false);
    try {
      const { data } = await api.post("/analyze", {
        image_base64: preview,
        display_name: displayName,
        make_public: makePublic,
      });
      if (user) refresh();
      navigate(`/r/${data.id}`, { state: { result: data } });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setLimitHit(true);
      } else if (status === 422) {
        toast.error(e.response?.data?.detail || "No clear face detected. Try a clearer photo.");
      } else {
        toast.error("Analysis failed. Please try again.");
      }
      setScanning(false);
    }
  };

  return (
    <div
      className="relative w-full rounded-xl border border-border bg-card p-5 shadow-2xl"
      data-testid="face-scanner"
    >
      <div className="mb-4 flex items-center justify-between">
        <span className="label-eyebrow">Live Analysis</span>
        <span className="flex items-center gap-1.5 text-xs text-success">
          <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" /> Engine online
        </span>
      </div>

      {/* Camera view */}
      <AnimatePresence>
        {camOpen && (
          <div className="relative mb-4 overflow-hidden rounded-lg border border-border">
            <video ref={videoRef} className="aspect-[3/4] w-full object-cover" playsInline muted />
            <div className="absolute inset-x-0 bottom-0 flex justify-center gap-3 p-4">
              <button onClick={capture} data-testid="capture-photo-btn" className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">
                Capture
              </button>
              <button onClick={closeCamera} className="rounded-full border border-border bg-card px-4 py-2.5 text-sm">
                Cancel
              </button>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Upload / preview */}
      {!camOpen && (
        <>
          {!preview ? (
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
              data-testid="upload-dropzone"
              className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-secondary/30 text-center transition hover:border-primary/50"
            >
              <Upload className="mb-3 h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">Drop a selfie or click to upload</p>
              <p className="mt-1 text-xs text-muted-foreground">Front-facing, good lighting. JPG or PNG.</p>
            </div>
          ) : (
            <div className="relative overflow-hidden rounded-lg border border-border">
              <img src={preview} alt="preview" className="aspect-[4/3] w-full object-cover" data-testid="scan-preview" />
              {scanning && (
                <>
                  <div className="scan-beam animate-scan" />
                  <div className="absolute inset-0 flex items-end justify-center bg-black/40 pb-6">
                    <div className="flex items-center gap-2 rounded-full bg-card/90 px-4 py-2 text-sm">
                      <Sparkles className="h-4 w-4 animate-pulse text-primary" />
                      Analysing 1,000+ data points…
                    </div>
                  </div>
                </>
              )}
              {!scanning && (
                <button onClick={reset} className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white" aria-label="Remove">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {!preview && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button onClick={() => fileRef.current?.click()} data-testid="choose-file-btn" className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-secondary/40 py-2.5 text-sm font-medium transition hover:border-primary/50">
                <Upload className="h-4 w-4" /> Upload photo
              </button>
              <button onClick={openCamera} data-testid="open-camera-btn" className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-secondary/40 py-2.5 text-sm font-medium transition hover:border-primary/50">
                <Camera className="h-4 w-4" /> Use camera
              </button>
            </div>
          )}

          {preview && !scanning && (
            <div className="mt-4 space-y-3">
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Display name (optional)"
                data-testid="display-name-input"
                className="w-full rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:border-primary/60"
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={makePublic} onChange={(e) => setMakePublic(e.target.checked)} data-testid="make-public-toggle" className="accent-primary" />
                Make my FaceCard public &amp; eligible for the leaderboard
              </label>
              <button
                onClick={analyze}
                data-testid="analyze-btn"
                className="w-full rounded-md bg-primary py-3 text-sm font-semibold text-primary-foreground transition hover:brightness-110"
              >
                Analyse my face — free
              </button>
            </div>
          )}

          {limitHit && (
            <div className="mt-4 rounded-md border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="limit-banner">
              <p className="flex items-center gap-2 font-medium"><Lock className="h-4 w-4 text-primary" /> Monthly free scans used up</p>
              <p className="mt-1 text-muted-foreground">Upgrade to Pro for 150 scans/month, progress tracking and the full coach plan.</p>
              <button onClick={() => navigate("/pricing")} className="mt-3 rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">
                See plans
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
